import { useCallback, useEffect, useState } from 'react';
import { useSubpantalla } from '../ui/navegacionReact';
import {
  ClipboardCheck, Plus, X, PackageSearch, ShieldAlert,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import ChipEstado from '../ui/ChipEstado';
import {
  ESTADO_CALIDAD_LOTE, ESTADO_INSPECCION, RESULTADO_INSPECCION,
} from '../ui/estados';
import FichaInspeccion from './FichaInspeccion';
import { useAuth } from '../lib/contextoAuth';
import { juzgar, rangoDe, cuantoFalta } from '../ui/calidad';
import { Cargando } from '../ui/EstadoDePantalla';

// INSPECCIONES · Fase 3 del módulo de Calidad.
//
// QUÉ CONTESTA ESTA PANTALLA. Dos preguntas, y en este orden porque es
// el orden en que se hacen:
//
//   1. «¿Qué tengo retenido esperando a que alguien lo mire?» — es lo
//      primero que se ve, arriba del todo. Es lo que abre el de calidad
//      al llegar por la mañana, y una lista de inspecciones pasadas no
//      contesta eso.
//   2. «Enséñame la inspección del lote tal.» — la lista de abajo.
//
// TRES DECISIONES DE USUARIO CERO QUE GOBIERNAN ESTO:
//
//   1. **Lo retenido va primero y con su propio color.** Ámbar, que es
//      lo que en esta casa significa «atención» (regla 10).
//   2. **Capturar una inspección enseña el plan ya desplegado**, con
//      cada cota y su rango debajo. Un formulario en blanco donde hay
//      que acordarse de qué se mide es como se saltan cosas.
//   3. **El OK/NOK no se pregunta.** Sale solo de comparar lo que se
//      teclea con el plan, en vivo, mientras se escribe (regla 8): así
//      quien mide ve la cota en rojo en el momento, no al final.
//
// Y UNA CUARTA, DESDE LA FASE 5: **con qué se midió se elige de una
// lista, y solo si esa lista existe.** Una empresa sin instrumentos
// dados de alta no ve el selector y captura exactamente igual que ayer
// —el campo de texto libre del plan sigue ahí—. Quien sí los tiene ve
// la vigencia de cada uno EN LA LISTA, antes de elegirlo: descubrir que
// el vernier está vencido al pulsar «guardar», con veinte medidas ya
// tecleadas, es tarde.

const TIPOS = [
  ['RECIBO', 'De recibo — al material que llega'],
  ['PROCESO', 'En proceso — mientras se fabrica'],
  ['FINAL', 'Final — antes de embarcar'],
];

// ---------------------------------------------------------------------
// Capturar una inspección
// ---------------------------------------------------------------------
function FormularioAlta({ skuInicial, loteInicial, onCreada, onCancelar }) {
  const [sku, setSku] = useState(skuInicial || '');
  const [lote, setLote] = useState(loteInicial || '');
  const [tipo, setTipo] = useState('RECIBO');
  const [plan, setPlan] = useState(null);
  const [buscandoPlan, setBuscandoPlan] = useState(false);
  const [sinPlan, setSinPlan] = useState(false);
  const [valores, setValores] = useState({});
  const [instrumentos, setInstrumentos] = useState([]);
  const [conQue, setConQue] = useState({});
  const [motivos, setMotivos] = useState({});
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // EL CATÁLOGO SE PIDE UNA VEZ Y SIN RUIDO. Si la empresa no tiene
  // ninguno —o quien captura no puede verlos— la lista se queda vacía y
  // el selector no aparece: la pantalla es la de la Fase 3, entera.
  useEffect(() => {
    let vivo = true;
    api.get('/instrumentos')
      .then((d) => { if (vivo) setInstrumentos(d.instrumentos); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  // El plan se busca en cuanto hay producto y tipo. Pedirle a alguien que
  // pulse «buscar» para ver qué tiene que medir es un paso de más en la
  // pantalla que se usa con una mano.
  useEffect(() => {
    let vivo = true;
    if (!sku.trim()) { setPlan(null); setSinPlan(false); return undefined; }
    setBuscandoPlan(true);
    (async () => {
      try {
        const lista = await api.get(
          `/inspecciones/planes?sku=${encodeURIComponent(sku.trim().toUpperCase())}`);
        const elegido = lista.planes.find((p) => p.tipo === tipo && p.activo);
        if (!vivo) return;
        if (!elegido) { setPlan(null); setSinPlan(true); return; }
        const detalle = await api.get(`/inspecciones/planes/${elegido.id}`);
        if (!vivo) return;
        setPlan(detalle);
        setSinPlan(false);
        setValores({});
      } catch {
        if (vivo) { setPlan(null); setSinPlan(true); }
      } finally {
        if (vivo) setBuscandoPlan(false);
      }
    })();
    return () => { vivo = false; };
  }, [sku, tipo]);

  const caracteristicas = plan?.caracteristicas || [];
  const sinLlenar = caracteristicas.filter((c) => {
    const v = valores[c.orden];
    return c.clase === 'VARIABLE'
      ? (v === undefined || v === '')
      : v === undefined;
  });

  // CON UN INSTRUMENTO VENCIDO HACE FALTA EXPLICAR POR QUÉ, y se dice
  // aquí y no al guardar. El servidor lo vuelve a exigir —es él quien
  // manda— pero enterarse después de teclear veinte medidas es un
  // regaño, no una ayuda.
  const elegido = (orden) => instrumentos.find((x) => x.id === conQue[orden]);
  const sinMotivo = caracteristicas.find((c) => {
    const i = elegido(c.orden);
    return i && !i.vigente && !(motivos[c.orden] || '').trim();
  });

  const falta = !sku.trim() ? 'el producto'
    : !lote.trim() ? 'el lote'
    : sinPlan ? null
    : !plan ? 'que cargue el plan'
    : sinLlenar.length > 0
      ? `llenar ${sinLlenar.length} ${sinLlenar.length === 1 ? 'cosa' : 'cosas'}`
      : sinMotivo
        ? `explicar por qué se mide «${sinMotivo.nombre}» con un instrumento sin calibración vigente`
        : null;

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      const mediciones = caracteristicas.map((c) => {
        const m = c.clase === 'VARIABLE'
          ? { orden: c.orden, valor: Number(valores[c.orden]) }
          : { orden: c.orden, atributo: !!valores[c.orden] };
        if (conQue[c.orden]) {
          m.id_instrumento = conQue[c.orden];
          if ((motivos[c.orden] || '').trim()) {
            m.motivo_instrumento_vencido = motivos[c.orden].trim();
          }
        }
        return m;
      });
      const d = await api.post('/inspecciones', {
        sku, codigo_lote: lote, tipo, id_plan: plan.id, observaciones, mediciones,
      });
      onCreada(d);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={enviar} className="border border-gray-200 rounded-xl p-4 space-y-4 bg-white">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Registrar una inspección</h3>
        <button type="button" onClick={onCancelar}
                className="text-gray-500 hover:text-gray-700" aria-label="Cancelar">
          <X size={18} />
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Producto</span>
          <input
            value={sku} onChange={(e) => setSku(e.target.value)} placeholder="TUB-1020"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-base"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Lote</span>
          <input
            value={lote} onChange={(e) => setLote(e.target.value)} placeholder="C-4471"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-base"
          />
          <span className="block text-xs text-gray-500 mt-1">
            El lote tiene que existir ya: se inspecciona mercancía que entró.
          </span>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Cuál es</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
            {TIPOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
      </div>

      {buscandoPlan && <div className="text-sm text-gray-500">Buscando el plan…</div>}

      {sinPlan && !buscandoPlan && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <strong>{sku.trim().toUpperCase()} no tiene un plan para esto.</strong>{' '}
          Sin plan no hay contra qué comparar lo que midas. Créalo en «Qué se
          revisa de cada producto» y vuelve.
        </div>
      )}

      {plan && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-gray-900">
            {plan.nombre} · versión {plan.version}
          </div>
          {caracteristicas.map((c) => {
            const v = valores[c.orden];
            const juicio = v === undefined ? null : juzgar(c, v, v);
            return (
              <div key={c.orden}
                   className="border border-gray-200 rounded-xl p-3 bg-gray-50 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-900">{c.nombre}</span>
                  {juicio && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                      juicio === 'OK' ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'}`}>
                      {juicio === 'OK' ? 'Dentro' : 'Fuera de tolerancia'}
                    </span>
                  )}
                </div>
                {rangoDe(c) && (
                  <div className="text-xs text-gray-600">{rangoDe(c)}</div>
                )}
                {c.instrumento_requerido && (
                  <div className="text-xs text-gray-500">
                    Con {c.instrumento_requerido}
                  </div>
                )}

                {instrumentos.length > 0 && (
                  <div className="space-y-1">
                    <label className="block">
                      <span className="block text-xs font-medium text-gray-700">
                        Con qué instrumento
                      </span>
                      <select
                        value={conQue[c.orden] || ''}
                        onChange={(e) => setConQue((a) => ({
                          ...a, [c.orden]: e.target.value ? Number(e.target.value) : null,
                        }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">
                          No lo apunto (se queda como hasta ahora)
                        </option>
                        {instrumentos.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.codigo} · {x.descripcion}
                            {x.vigente ? '' : ` — ${x.vigencia_nombre.toLowerCase()}`}
                          </option>
                        ))}
                      </select>
                    </label>
                    {elegido(c.orden) && !elegido(c.orden).vigente && (
                      <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg p-2 space-y-1">
                        <div>
                          <strong>{elegido(c.orden).codigo} no está calibrado.</strong>{' '}
                          {cuantoFalta(elegido(c.orden))} Elige otro, o explica
                          aquí por qué hay que medir con éste — es lo que va a
                          leer un auditor. Autorizarlo necesita permiso.
                        </div>
                        <textarea
                          value={motivos[c.orden] || ''}
                          onChange={(e) => setMotivos((a) => ({
                            ...a, [c.orden]: e.target.value,
                          }))}
                          rows={2}
                          placeholder="Es el único que llega a esa cota y la pieza sale hoy"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
                        />
                      </div>
                    )}
                  </div>
                )}

                {c.clase === 'VARIABLE' ? (
                  <input
                    type="number" step="any"
                    value={v === undefined ? '' : v}
                    onChange={(e) => setValores((a) => ({ ...a, [c.orden]: e.target.value }))}
                    placeholder="Lo que midió"
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-base"
                  />
                ) : (
                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setValores((a) => ({ ...a, [c.orden]: true }))}
                      className={`flex-1 rounded-lg px-4 py-3 text-sm font-semibold border ${
                        v === true ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-800 border-gray-300'}`}
                    >
                      Sí, cumple
                    </button>
                    <button
                      type="button"
                      onClick={() => setValores((a) => ({ ...a, [c.orden]: false }))}
                      className={`flex-1 rounded-lg px-4 py-3 text-sm font-semibold border ${
                        v === false ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-800 border-gray-300'}`}
                    >
                      No cumple
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          <label className="block">
            <span className="block text-sm font-medium text-gray-700">
              Observaciones
            </span>
            <textarea
              value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
              rows={2} placeholder="Opcional"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </label>
        </div>
      )}

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      <div className="flex items-center gap-3">
        <button
          type="submit" disabled={!!falta || sinPlan || guardando}
          className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {guardando ? 'Guardando…' : 'Guardar como borrador'}
        </button>
        {falta && <span className="text-sm text-gray-500">Falta {falta}.</span>}
      </div>
      <p className="text-xs text-gray-500">
        Se guarda como borrador: no cuenta para nada. Firmarla es el
        paso siguiente, y ése ya no se puede deshacer.
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------
// La pantalla
// ---------------------------------------------------------------------
export default function Inspecciones() {
  const { puede } = useAuth();
  const [retenidos, setRetenidos] = useState(null);
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  const [alta, setAlta] = useState(null);
  const [abierta, setAbierta] = useState(null);
  useSubpantalla('inspeccion', !!abierta, () => setAbierta(null));

  // Sin `async` en el cuerpo del efecto, que es el patrón de la casa
  // (`Documentos.jsx`): lo que carga se resuelve en el `then`, no de
  // forma síncrona dentro del efecto.
  const cargar = useCallback(() => {
    Promise.all([
      api.get('/inspecciones/por-liberar'),
      api.get('/inspecciones'),
    ])
      .then(([r, l]) => { setRetenidos(r.lotes); setLista(l.inspecciones); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.'));
  }, []);

  useEffect(cargar, [cargar]);

  async function abrir(id) {
    try {
      setAbierta(await api.get(`/inspecciones/${id}`));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo abrir.');
    }
  }

  if (abierta) {
    return (
      <FichaInspeccion
        inspeccion={abierta}
        onVolver={() => { setAbierta(null); cargar(); }}
        onCambio={cargar}
      />
    );
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold text-gray-900">Inspecciones</h1>
        <p className="text-sm text-gray-600">
          Lo que está esperando a que alguien lo revise, y las revisiones que ya
          se hicieron con su firma.
        </p>
      </header>

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      {alta !== null ? (
        <FormularioAlta
          skuInicial={alta.sku} loteInicial={alta.codigo_lote}
          onCancelar={() => setAlta(null)}
          onCreada={(d) => { setAlta(null); setAbierta(d); cargar(); }}
        />
      ) : puede('calidad.inspeccionar') && (
        <button
          onClick={() => setAlta({})}
          className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
        >
          <Plus size={16} /> Registrar una inspección
        </button>
      )}

      {/* LO RETENIDO VA PRIMERO. Es lo que reclama la acción de alguien. */}
      {retenidos !== null && retenidos.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <ShieldAlert size={18} className="text-amber-600" />
            Esperando decisión ({retenidos.length})
          </h2>
          {retenidos.map((l) => (
            <div key={l.id}
                 className="border border-amber-200 bg-amber-50 rounded-xl p-3 flex items-start gap-3">
              <PackageSearch className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900">
                  {l.sku} · lote {l.codigo_lote}
                </div>
                <div className="text-sm text-gray-700">{l.producto}</div>
                <div className="text-sm text-gray-700">
                  {l.inspecciones_firmadas === 0
                    ? 'Nadie lo ha inspeccionado todavía.'
                    : `${l.inspecciones_firmadas} inspección(es) firmada(s).`}
                  {' '}Hay {l.existencia} piezas.
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <ChipEstado mapa={ESTADO_CALIDAD_LOTE} clave={l.estado_calidad} />
                {puede('calidad.inspeccionar') && (
                  <button
                    onClick={() => setAlta({ sku: l.sku, codigo_lote: l.codigo_lote })}
                    className="text-sm font-semibold text-[var(--color-marca)] hover:underline"
                  >
                    Inspeccionar
                  </button>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      {lista === null && <Cargando filas={4} />}

      {lista !== null && lista.length === 0 && alta === null && (
        <EstadoVacio
          icono={ClipboardCheck}
          titulo="Todavía no hay ninguna inspección"
          descripcion={
            'Una inspección es la revisión de un lote contra lo que se decidió '
            + 'revisarle: se toman las medidas, se firma, y a partir de ahí ya '
            + 'no se puede cambiar. Es lo que un auditor pide para creerse que '
            + 'la mercancía se revisó.'
          }
          accion={puede('calidad.inspeccionar') && (
            <button
              onClick={() => setAlta({})}
              className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
            >
              <Plus size={16} /> Registrar la primera
            </button>
          )}
        />
      )}

      {lista !== null && lista.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-semibold text-gray-900">Lo que ya se revisó</h2>
          <NotaExplicativa titulo="Una inspección firmada no se puede modificar">
            Es lo que la vuelve una evidencia. Si hay que corregir algo, se
            registra otra inspección que haga referencia a la anterior y explique
            el motivo — las dos se conservan.
          </NotaExplicativa>
          {lista.map((i) => (
            <button
              key={i.id} onClick={() => abrir(i.id)}
              className="w-full text-left border border-gray-200 rounded-xl p-3 bg-white hover:border-gray-300"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900">
                    {i.folio} · {i.sku} · lote {i.codigo_lote}
                  </div>
                  <div className="text-sm text-gray-600">
                    {i.tipo_nombre}
                    {i.firmante ? ` · firmó ${i.firmante}` : ''}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <ChipEstado mapa={ESTADO_INSPECCION} clave={i.estado} />
                  <ChipEstado mapa={RESULTADO_INSPECCION} clave={i.resultado} />
                </div>
              </div>
            </button>
          ))}
        </section>
      )}
    </div>
  );
}
